import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import StoryImport from './StoryImport'; // Import the new component
import SpeechToText from './SpeechToText'; // Import the SpeechToText component

const SummaryInput = ({ onGenerate, onUserPreFedStory, isLoading, summaries, onSelectSummary }) => {
  const [summary, setSummary] = useState('');
  const [commitId, setCommitId] = useState('');

  const handleSpeechResult = (transcript) => {
    setSummary(summary + ' ' + transcript);
  };

  const onPreFedStory = () => {
    onUserPreFedStory();
  };

  useEffect(() => {
    fetch('/commit-id.txt')
      .then((res) => res.text())
      .then((text) => setCommitId(text.trim()))
      .catch(() => setCommitId('Unknown')); // Fallback in case of an error
  }, []);

  // Handle summary card click
  const handleSummaryClick = (summaryText) => {
    setSummary(summaryText); // Paste the summary into the input prompt
    onSelectSummary(summaryText); // Call onSelectSummary to trigger the API call
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 3,
        backgroundColor: '#f5f5f5', // Optional: Add a light background color
        justifyContent: summaries.length > 0 ? 'flex-start' : 'center', // Center content initially, move up when summaries are generated
        boxSizing: 'border-box', // Add this
        height: '100dvh', // ✅ Set height to 100vh
      }}
    >
      {/* Title */}
      <Typography variant="h4" gutterBottom>
        Storybuilder
      </Typography>

      {/* Input Field */}
      <Box sx={{ width: '100%', maxWidth: '600px', mb: 4 }}>
        <TextField
          label="Write your story summary here..."
          multiline
          rows={4}
          fullWidth
          variant="outlined"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          margin="normal"
          disabled={isLoading} // Disable input field while loading
          InputProps={{
            endAdornment: (
              <SpeechToText 
                disabled = {isLoading}
                onResult={handleSpeechResult} 
              />
            ),
          }}
        />
      </Box>

      {/* Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onGenerate(summary)} // Pass the summary text to handleGenerate
          disabled={!summary.trim() || isLoading} // Disable button while loading or if input is empty
          sx={{ width: '200px' }} // Fixed width to prevent button resizing
        >
          {isLoading ? (
            <CircularProgress size={24} sx={{ color: 'white' }} /> // Show spinner when loading
          ) : (
            'Generate Story' // Show text when not loading
          )}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onPreFedStory}
          disabled={isLoading} // Disable button while loading
        >
          Use Tortoise & Hare Story
        </Button>

        {/* Import Button for File Upload */}
        <StoryImport
          onValidContent={onGenerate} // Pass the handleGenerate function directly
          disabled={isLoading} // Disable button while loading
        />
      </Box>

      {/* Summary Cards */}
      {summaries.length > 0 && (
        <Box sx={{ width: '100%', maxWidth: '1200px', mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Generated Summaries
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {summaries.map((summaryObj, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#e3f2fd' }, // Light blue on hover
                    border: summary === summaryObj.summary ? '2px solid #4caf50' : 'none', // Green border if selected
                    height: '100%', // Ensure the card height fits its content
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%', // Ensure the card takes up the full width of the grid item
                    maxWidth: '400px', // Optional: Limit the maximum width of each card
                  }}
                  onClick={() => handleSummaryClick(summaryObj.summary)} // Handle summary click
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Render the title */}
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {summaryObj.title}
                    </Typography>
                    {/* Render the summary text */}
                    <Typography variant="body1">{summaryObj.summary}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Build ID */}
      {commitId && (
        <Typography
          variant="caption"
          sx={{
            position: 'fixed',
            bottom: 10,
            right: 10,
            color: 'gray',
          }}
        >
          Build ID: {commitId}
        </Typography>
      )}
    </Box>
  );
};

export default SummaryInput;